import React, { useState, useEffect } from 'react';
import { Card, Title, Text, Button, Badge, Group, Stack, Alert, Modal, TextInput } from '@mantine/core';
import { Check, Star, AlertCircle, Lock, Trash2 } from 'lucide-react';
import { usePremiumFeatures } from '../../contexts/PremiumContext';
import { useUser } from '../../contexts/UserContext';
import { paymentsApi } from '../../api/paymentsApi';
import { userApi } from '../../api/userApi';
import { useMutation } from '@tanstack/react-query';
import { useSquarePayments } from '../../hooks/useSquarePayments';
import { DeleteAccountModal } from '../DeleteAccountModal';
import './PlanPage.css';

export const PlanPage: React.FC = () => {
  const { isPremium, subscriptionStatus, subscriptionData, refreshSubscription, isLoading } = usePremiumFeatures();
  const { currentUser } = useUser();
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<{ message: string; type: 'info' | 'error' | 'success' } | null>(null);
  const [adminDeleteEmail, setAdminDeleteEmail] = useState('');
  const [adminDeleteStatus, setAdminDeleteStatus] = useState<{ message: string; type: 'error' | 'success' } | null>(null);
  const [adminPremiumStatus, setAdminPremiumStatus] = useState<{ message: string; type: 'error' | 'success' } | null>(null);
  const [cardReady, setCardReady] = useState(false);
  const [cardAttaching, setCardAttaching] = useState(false);
  const { isReady: squareReady, isLoading: squareLoading, error: squareError, tokenizeCard, attachCard, cleanupCard } = useSquarePayments();

  const isAdminUser = (currentUser?.email || '').toLowerCase() === 'olahalvorsen@gmail.com';

  const createSubscriptionMutation = useMutation({
    mutationFn: paymentsApi.createSubscription,
    onSuccess: () => {
      setPaymentStatus({ message: 'Subscription created successfully! 🎉', type: 'success' });
      setTimeout(() => {
        setShowPaymentForm(false);
        setPaymentStatus(null);
        refreshSubscription();
      }, 2000);
    },
    onError: (error: any) => {
      console.error('Subscription creation error:', error);
      
      // Handle 409 Conflict (duplicate subscription)
      if (error?.response?.status === 409) {
        const errorData = error.response?.data;
        setPaymentStatus({ 
          message: errorData?.error || 'You already have an active subscription. Only one subscription is allowed per account.', 
          type: 'error' 
        });
      } else {
        setPaymentStatus({ 
          message: error instanceof Error ? error.message : 'Failed to create subscription', 
          type: 'error' 
        });
      }
    }
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => paymentsApi.cancelSubscription(currentUser?.email || ''),
    onSuccess: () => {
      refreshSubscription();
    }
  });

  const adminDeleteUserMutation = useMutation({
    mutationFn: (email: string) => userApi.adminDeleteUser(email),
    onSuccess: (result) => {
      setAdminDeleteStatus({ message: result.message || 'User deleted', type: 'success' });
    },
    onError: (error: any) => {
      setAdminDeleteStatus({
        message: error instanceof Error ? error.message : 'Failed to delete user',
        type: 'error'
      });
    }
  });

  const adminGrantPremiumMutation = useMutation({
    mutationFn: () => userApi.adminGrantPremium(),
    onSuccess: (result) => {
      setAdminPremiumStatus({ message: result.message || 'Premium granted', type: 'success' });
      refreshSubscription();
    },
    onError: (error: any) => {
      setAdminPremiumStatus({
        message: error instanceof Error ? error.message : 'Failed to grant premium',
        type: 'error'
      });
    }
  });

  const adminRevokePremiumMutation = useMutation({
    mutationFn: () => userApi.adminRevokePremium(),
    onSuccess: (result) => {
      setAdminPremiumStatus({ message: result.message || 'Premium revoked', type: 'success' });
      refreshSubscription();
    },
    onError: (error: any) => {
      setAdminPremiumStatus({
        message: error instanceof Error ? error.message : 'Failed to revoke premium',
        type: 'error'
      });
    }
  });

  const handleUpgradeClick = () => {
    // Prevent opening if user already has premium
    if (isPremium) {
      console.warn('User already has an active premium subscription');
      return;
    }
    
    setShowPaymentForm(true);
    setPaymentStatus(null);
    setCardReady(false);
    setCardAttaching(false);
  };

  // Initialize Square card when modal opens - only once
  useEffect(() => {
    if (showPaymentForm && squareReady && !cardAttaching && !cardReady) {
      console.log('Starting card initialization...');
      
      const initCard = async () => {
        setCardAttaching(true);
        try {
          // Wait for DOM to be fully rendered
          await new Promise(resolve => setTimeout(resolve, 500));
          
          const cardContainer = document.getElementById('card-container');
          if (!cardContainer) {
            throw new Error('Card container element not found in DOM');
          }
          
          console.log('Card container found, attaching card...');
          await attachCard('#card-container');
          setCardReady(true);
          console.log('Card attached successfully');
        } catch (error) {
          console.error('Failed to attach card:', error);
          setPaymentStatus({ 
            message: 'Failed to initialize payment form. Please refresh and try again.', 
            type: 'error' 
          });
        } finally {
          setCardAttaching(false);
        }
      };
      
      // Start initialization with longer delay
      const timeoutId = setTimeout(initCard, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [showPaymentForm, squareReady, cardAttaching, cardReady]);

  // Cleanup when modal closes
  useEffect(() => {
    if (!showPaymentForm) {
      setCardReady(false);
      setCardAttaching(false);
      setPaymentStatus(null);
      cleanupCard();
    }
  }, [showPaymentForm, cleanupCard]);

  const handlePaymentSubmit = async () => {
    if (!currentUser?.email) return;

    try {
      setPaymentStatus({ message: 'Securing payment information...', type: 'info' });
      
      // Tokenize payment method with Square
      const tokenResult = await tokenizeCard();
      console.log('Square tokenization result:', tokenResult);
      
      if (tokenResult.status === 'OK' && tokenResult.token) {
        setPaymentStatus({ message: 'Processing subscription...', type: 'info' });
        
        const subscriptionRequest = {
          sourceId: tokenResult.token,
          userEmail: currentUser.email,
          planId: 'premium-monthly'
        };
        
        console.log('Creating subscription with:', subscriptionRequest);
        createSubscriptionMutation.mutate(subscriptionRequest);
      } else {
        // Handle tokenization errors
        let errorMessage = 'Payment information could not be processed.';
        
        if (tokenResult.errors && tokenResult.errors.length > 0) {
          const errorMessages = tokenResult.errors.map(error => {
            switch (error.code) {
              case 'CARD_EXPIRED':
                return 'Your card has expired. Please use a different payment method.';
              case 'INVALID_CARD':
                return 'Please check your card information and try again.';
              case 'CVV_INVALID':
                return 'Please check your card security code (CVV).';
              case 'POSTAL_CODE_INVALID':
                return 'Please check your postal code.';
              default:
                return error.message || error.detail || 'Payment information is invalid.';
            }
          });
          errorMessage = errorMessages.join('\n');
        }
        
        setPaymentStatus({ message: errorMessage, type: 'error' });
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentStatus({ 
        message: error instanceof Error ? error.message : 'An error occurred while processing your payment', 
        type: 'error' 
      });
    }
  };

  const handleCancelSubscription = () => {
    if (window.confirm('Are you sure you want to cancel your premium subscription?')) {
      cancelSubscriptionMutation.mutate();
    }
  };

  const premiumFeatures = [
    'Kanban boards with customizable columns',
    'Calendar view with drag & drop planning',
    'Advanced reporting and analytics',
    'Unlimited notes and attachments',
    'Priority email support'
  ];

  if (isLoading) {
    return (
      <div className="plan-page-loading">
        <Text>Loading subscription information...</Text>
      </div>
    );
  }

  return (
    <div className="plan-page">
      <Title order={2} mb="xl" style={{ color: 'var(--app-text-primary)' }}>Subscription Plan</Title>
      
      {squareError && (
        <Alert color="orange" icon={<AlertCircle size={16} />} mb="xl">
          <Text size="sm" fw={500}>Premium subscriptions are in development</Text>
          <Text size="xs" c="dimmed" mt={4}>
            Square payment system is being configured. Premium features are protected but payment processing is not yet available.
            {squareError.includes('not properly configured') && (
              <><br />Technical: Square sandbox credentials need to be configured.</>
            )}
          </Text>
        </Alert>
      )}
      
      {/* Current Plan Status */}
      <Card withBorder padding="xl" mb="xl" className="current-plan-card">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group align="center" mb="xs">
              <Text size="lg" fw={600}>
                {isPremium ? 'Premium Plan' : 'Free Plan'}
              </Text>
              <Badge color={isPremium ? 'green' : 'gray'} size="lg">
                {subscriptionStatus}
              </Badge>
            </Group>
            
            <Text size="sm" c="dimmed" mb="md">
              {isPremium 
                ? 'A$4.95 AUD/month (charged in AUD; your bank may convert)' 
                : 'Limited features'
              }
            </Text>

            {subscriptionData?.currentSubscription && (
              <Stack gap="xs">
                <Text size="sm">
                  <strong>Plan:</strong> {subscriptionData.currentSubscription.planName}
                </Text>
                <Text size="sm">
                  <strong>Started:</strong> {new Date(subscriptionData.currentSubscription.startDate).toLocaleDateString()}
                </Text>
                {subscriptionData.currentSubscription.endDate && (
                  <Text size="sm">
                    <strong>Ends:</strong> {new Date(subscriptionData.currentSubscription.endDate).toLocaleDateString()}
                  </Text>
                )}
              </Stack>
            )}
          </div>
          
          <div>
            {!isPremium && (
              <Button 
                onClick={handleUpgradeClick}
                size="md"
                leftSection={<Star size={16} />}
                loading={createSubscriptionMutation.isPending}
              >
                Upgrade to Premium
              </Button>
            )}
            
            {isPremium && subscriptionStatus === 'Active' && (
              <Button 
                variant="light" 
                color="red" 
                onClick={handleCancelSubscription}
                size="md"
                loading={cancelSubscriptionMutation.isPending}
              >
                Cancel Subscription
              </Button>
            )}
          </div>
        </Group>
      </Card>

      {/* Premium Features */}
      <Card withBorder padding="xl" mb="xl">
        <Title order={3} mb="md">Premium Features</Title>
        
        <Stack gap="sm">
          {premiumFeatures.map((feature, index) => (
            <Group key={index} align="flex-start">
              <Check size={18} color="var(--mantine-color-green-6)" style={{ marginTop: 2 }} />
              <Text size="sm">{feature}</Text>
            </Group>
          ))}
        </Stack>

        {!isPremium && (
          <Alert icon={<AlertCircle size={16} />} title="Upgrade to unlock all features" color="blue" mt="md">
            Get access to advanced productivity features for just A$4.95 AUD per month.
            Charges are in AUD; your bank may apply currency conversion.
          </Alert>
        )}
      </Card>

      {/* Payment History */}
      {subscriptionData?.currentSubscription && (
        <Card withBorder padding="xl">
          <Title order={3} mb="md">Billing Information</Title>
          
          <Stack gap="sm">
            <Text size="sm">
              <strong>Amount:</strong> {subscriptionData.currentSubscription.currency} {subscriptionData.currentSubscription.amount}
            </Text>
            <Text size="sm">
              <strong>Billing Period:</strong> {subscriptionData.currentSubscription.billingPeriod}
            </Text>
            <Text size="sm">
              <strong>Status:</strong> {subscriptionData.currentSubscription.status}
            </Text>
          </Stack>
        </Card>
      )}

      {/* Delete Account - Only visible for free users */}
      {!isPremium && (
        <Card withBorder padding="xl" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
          <Title order={3} mb="md" c="red">Danger Zone</Title>
          
          <Stack gap="md">
            <div>
              <Text size="sm" fw={600} mb="xs">Delete Account</Text>
              <Text size="sm" c="dimmed">
                Permanently delete your account and all associated data. This action cannot be undone.
              </Text>
            </div>
            
            <Button
              color="red"
              variant="outline"
              leftSection={<Trash2 size={16} />}
              onClick={() => setShowDeleteAccountModal(true)}
            >
              Delete My Account
            </Button>
          </Stack>
        </Card>
      )}

      {/* Admin-only user deletion */}
      {isAdminUser && (
        <Card withBorder padding="xl" mt="xl" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
          <Title order={3} mb="md" c="red">Admin</Title>

          <Stack gap="md">
            <div>
              <Text size="sm" fw={600} mb="xs">Delete User</Text>
              <Text size="sm" c="dimmed">
                Delete a user and all associated data by email.
              </Text>
            </div>

            <TextInput
              label="User email"
              placeholder="user@example.com"
              value={adminDeleteEmail}
              onChange={(e) => setAdminDeleteEmail(e.currentTarget.value)}
            />

            <Button
              color="red"
              variant="outline"
              leftSection={<Trash2 size={16} />}
              loading={adminDeleteUserMutation.isPending}
              onClick={() => {
                const target = adminDeleteEmail.trim();
                if (!target) return;
                if (window.confirm(`Permanently delete ${target}? This cannot be undone.`)) {
                  setAdminDeleteStatus(null);
                  adminDeleteUserMutation.mutate(target);
                }
              }}
            >
              Delete User
            </Button>

            {adminDeleteStatus && (
              <Alert color={adminDeleteStatus.type === 'error' ? 'red' : 'green'}>
                {adminDeleteStatus.message}
              </Alert>
            )}

            <div style={{ marginTop: 8 }}>
              <Text size="sm" fw={600} mb="xs">Grant Premium (Me)</Text>
              <Text size="sm" c="dimmed">
                Mark your account as Premium without payment.
              </Text>
            </div>

            <Button
              variant="outline"
              loading={adminGrantPremiumMutation.isPending}
              onClick={() => {
                if (window.confirm('Grant Premium to your account?')) {
                  setAdminPremiumStatus(null);
                  adminGrantPremiumMutation.mutate();
                }
              }}
            >
              Grant Premium
            </Button>

            <Button
              variant="outline"
              color="red"
              loading={adminRevokePremiumMutation.isPending}
              onClick={() => {
                if (window.confirm('Revoke Premium from your account?')) {
                  setAdminPremiumStatus(null);
                  adminRevokePremiumMutation.mutate();
                }
              }}
            >
              Revoke Premium
            </Button>

            {adminPremiumStatus && (
              <Alert color={adminPremiumStatus.type === 'error' ? 'red' : 'green'}>
                {adminPremiumStatus.message}
              </Alert>
            )}
          </Stack>
        </Card>
      )}

      {/* Payment Modal */}
      <Modal 
        opened={showPaymentForm} 
        onClose={() => {
          setShowPaymentForm(false);
          setCardReady(false);
          setCardAttaching(false);
          setPaymentStatus(null);
          cleanupCard();
        }}
        title="Upgrade to Premium"
        size="md"
        styles={{
          header: {
            backgroundColor: 'var(--app-surface)',
            borderBottom: '1px solid var(--app-border)',
            color: 'var(--app-text-primary)'
          },
          title: {
            color: 'var(--app-text-primary)',
            fontSize: '18px',
            fontWeight: 600
          },
          content: {
            backgroundColor: 'var(--app-surface)'
          },
          body: {
            backgroundColor: 'var(--app-surface)',
            color: 'var(--app-text-primary)'
          },
          close: {
            color: 'var(--app-text-secondary)'
          }
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed" style={{ marginTop: '16px' }}>
            Get access to advanced features like Kanban boards and Calendar view for A$4.95 AUD/month.
            <br />
            <em>Note: You will be charged in AUD; your bank may convert to your local currency</em>
          </Text>
          
          {/* Payment Form */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Text size="sm" fw={500}>Payment Information</Text>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px', 
                padding: '4px 12px', 
                backgroundColor: 'var(--mantine-color-green-0)', 
                border: '1px solid var(--mantine-color-green-3)', 
                borderRadius: '20px'
              }}>
                <Lock size={12} style={{ color: 'var(--mantine-color-green-6)' }} />
                <Text size="xs" style={{ color: 'var(--mantine-color-green-7)' }}>Square Payments</Text>
              </div>
            </div>
            
            {squareError && (
              <Alert color="red" icon={<AlertCircle size={16} />}>
                {squareError}
              </Alert>
            )}
            
            <div 
              id="card-container" 
              style={{ 
                padding: '16px', 
                border: '1px solid var(--app-border)', 
                borderRadius: '8px', 
                minHeight: '100px',
                backgroundColor: 'var(--app-surface)'
              }}
              className="payment-card-container"
            >
              {!cardReady && !paymentStatus && (
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '80px'
                }}>
                  <Text size="sm" style={{ color: 'var(--app-text-secondary)' }}>
                    {squareLoading
                      ? 'Loading payment system...'
                      : cardAttaching
                        ? 'Initializing payment form...'
                        : 'Preparing secure payment form...'}
                  </Text>
                </div>
              )}
              {/* Square payment form will be inserted here */}
            </div>
            
            {paymentStatus && (
              <Alert 
                color={paymentStatus.type === 'error' ? 'red' : paymentStatus.type === 'success' ? 'green' : 'blue'}
                mt="sm"
              >
                {paymentStatus.message}
              </Alert>
            )}
          </div>
          
          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={() => {
              setShowPaymentForm(false);
              setCardReady(false);
              setCardAttaching(false);
              setPaymentStatus(null);
              cleanupCard();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handlePaymentSubmit}
              loading={createSubscriptionMutation.isPending}
              disabled={!cardReady || !!paymentStatus || createSubscriptionMutation.isPending}
            >
              Subscribe for A$4.95/month
            </Button>
          </Group>

          <Text size="xs" style={{ color: 'var(--app-text-secondary)', textAlign: 'center' }}>
            💳 Secure payment processing powered by Square
          </Text>
        </Stack>
      </Modal>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        opened={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
      />
    </div>
  );
};